import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  AddPaymentGiftCardToBookingInput,
  useAddPaymentGiftCardToBookingMutation,
} from "./__generated__/useAddPaymentGiftCardToBookingMutation.graphql";

export const useAddPaymentGiftCardToBooking = () => {
  const { showToast } = useToast();
  const { patronLoyalty } = useFlags();
  const [addPaymentGiftCardToBooking, isAddingPaymentGiftCardToBooking] =
    useMutationWithError<useAddPaymentGiftCardToBookingMutation>(
      useAddPaymentGiftCardToBookingMutationString
    );

  const handleResult = (
    success: boolean | null | undefined,
    bookingId: string | null | undefined,
    successCallback: (bookingId: string) => void
  ) => {
    if (success) {
      successCallback(bookingId ?? "");
      showToast({
        description: t`Gift card added to booking.`,
        semanticStyle: "success",
        toastId: "add-payment-gift-card-to-booking-success",
      });
    } else {
      showToast({
        description: t`Sorry. An error occurred while adding a gift card to the booking.`,
        semanticStyle: "error",
        toastId: "add-payment-gift-card-to-booking-error",
      });
    }
  };

  const handleAddPaymentGiftCardToBooking = (
    input: AddPaymentGiftCardToBookingInput,
    successCallback: (bookingId: string) => void
  ) => {
    addPaymentGiftCardToBooking({
      variables: { input, patronLoyalty: !!patronLoyalty },
      onCompleted: (response) =>
        handleResult(
          response.responsePayload?.success,
          response.responsePayload?.booking?.id,
          successCallback
        ),
    });
  };

  return [
    handleAddPaymentGiftCardToBooking,
    isAddingPaymentGiftCardToBooking,
  ] as const;
};

export const useAddPaymentGiftCardToBookingMutationString = graphql`
  mutation useAddPaymentGiftCardToBookingMutation(
    $input: AddPaymentGiftCardToBookingInput!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: addPaymentGiftCardToBooking(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        totalDueAfterCredits
        projectedCreditApplications @include(if: $patronLoyalty) {
          amount
          paymentSource
          squareGiftCard {
            id
            ganLastFour
          }
          raincheck {
            id
          }
        }
        ... on StandardBooking {
          ownerClient {
            ... on Customer {
              squareGiftCards(active: true, hasNonZeroBalance: true) {
                edges {
                  node {
                    id
                    balance
                  }
                }
              }
            }
          }
          paymentGiftCards {
            edges {
              node {
                id
              }
            }
          }
        }
        ... on LessonBooking {
          ownerClient {
            ... on Customer {
              squareGiftCards(active: true, hasNonZeroBalance: true) {
                edges {
                  node {
                    id
                    balance
                  }
                }
              }
            }
          }
          paymentGiftCards {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
